import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useUpdateEvacuationPlan } from "./useEvacuationData";
import { useOrganisation } from "./useOrganisation";
import { EvacuationFormStep, EvacuationPlan } from "../types/evacuation";

interface UseEditEvacuationFormResult {
  name: string;
  setName: (name: string) => void;
  emergencyType: string;
  setEmergencyType: (type: string) => void;
  isActive: boolean;
  setIsActive: (active: boolean) => void;
  steps: EvacuationFormStep[];
  addStep: () => void;
  removeStep: (stepId: string | number) => void;
  updateStep: (stepId: string | number, description: string) => void;
  handleSave: () => void;
  isSaving: boolean;
}

export const useEditEvacuationForm = (
  planId: string | number | undefined,
  initialData: EvacuationPlan | undefined
): UseEditEvacuationFormResult => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { organisationId } = useOrganisation();
  const { mutate, isLoading: isSaving } = useUpdateEvacuationPlan();

  const [name, setName] = useState("");
  const [emergencyType, setEmergencyType] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [steps, setSteps] = useState<EvacuationFormStep[]>([]);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setEmergencyType(initialData.emergency_type);
      setIsActive(initialData.is_active);
      
      const formattedSteps: EvacuationFormStep[] = (initialData.steps || []).map((step) => ({
        id: step.id || crypto.randomUUID(),
        description: step.description,
        order: step.order,
      }));
      setSteps(formattedSteps);
    }
  }, [initialData]);

  const addStep = useCallback(() => {
    const newStep: EvacuationFormStep = {
      id: crypto.randomUUID(),
      description: "",
      order: steps.length + 1,
    };
    setSteps((prev) => [...prev, newStep]);
  }, [steps.length]);

  const removeStep = useCallback((stepId: string | number) => {
    setSteps((prev) => {
      if (prev.length <= 1) return prev;
      return prev
        .filter((step) => step.id !== stepId)
        .map((step, index) => ({ ...step, order: index + 1 }));
    });
  }, []);

  const updateStep = useCallback((stepId: string | number, description: string) => {
    setSteps((prev) =>
      prev.map((step) =>
        step.id === stepId ? { ...step, description } : step
      )
    );
  }, []);

  const handleSave = useCallback(() => {
    if (!planId || !organisationId) {
      enqueueSnackbar("Kunde inte hitta plan-ID eller organisations-ID.", {
        variant: "error",
      });
      return;
    }

    if (!name || !emergencyType) {
      enqueueSnackbar("Vänligen fyll i alla obligatoriska fält.", {
        variant: "error",
      });
      return;
    }

    const validSteps = steps.filter((step) => step.description.trim() !== "");
    if (validSteps.length === 0) {
      enqueueSnackbar("Lägg till minst ett steg i checklistan.", {
        variant: "error",
      });
      return;
    }

    const numericId = typeof planId === "string" ? parseInt(planId, 10) : planId;

    mutate(
      {
        id: numericId,
        data: {
          name,
          emergency_type: emergencyType,
          organisation_id: organisationId,
          is_active: isActive,
          steps: validSteps.map((step) => ({
            description: step.description,
            order: step.order,
          })),
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar("Checklista uppdaterad.", {
            variant: "success",
          });
          navigate(`/admin/evacuation/details/${planId}`);
        },
        onError: (error) => {
          enqueueSnackbar("Kunde inte uppdatera checklistan.", {
            variant: "error",
          });
          console.error("Error updating evacuation plan:", error);
        },
      }
    );
  }, [
    planId,
    organisationId,
    name,
    emergencyType,
    isActive,
    steps,
    mutate,
    navigate,
    enqueueSnackbar,
  ]);

  return {
    name,
    setName,
    emergencyType,
    setEmergencyType,
    isActive,
    setIsActive,
    steps,
    addStep,
    removeStep,
    updateStep,
    handleSave,
    isSaving,
  };
};
